import { DEFAULT_UNAVAILABLE_MESSAGE } from './productLabels';

export interface PurchaseLine {
  variantId: string;
  productTitle: string;
  isTextOnlyAddon?: boolean;
  attributes?: Array<{ key: string; value: string }>;
}

export const AVAILABILITY_CHECK_FAILED = 'Nie udało się sprawdzić dostępności produktów. Spróbuj ponownie za chwilę.';
export const LEGACY_ADDON_UNAVAILABLE = 'Nie można sprawdzić dostępności dodatku ze starszego koszyka. Usuń ten dodatek (lub produkt, do którego jest przypisany) i wybierz go ponownie.';

/** Always check live availability; catalog/prerender snapshots can be out of date. */
export async function checkPurchaseAvailability(lines: PurchaseLine[], endpoint: string): Promise<string | null> {
  const legacyAddon = lines.find(line => line.isTextOnlyAddon
    || (typeof line.variantId === 'string' && line.variantId.startsWith('addon-preview:'))
    || (Array.isArray(line.attributes) && line.attributes.some(attribute => attribute?.key === 'Dodatki'
      && (typeof attribute.value !== 'string' || attribute.value.trim()))));
  if (legacyAddon) return `${legacyAddon.productTitle}: ${LEGACY_ADDON_UNAVAILABLE}`;
  const checkedLines = lines;
  const variantIds = [...new Set(checkedLines.map(line => line.variantId))];
  if (!variantIds.length) return null;
  try {
    const responses = await Promise.all(Array.from({ length: Math.ceil(variantIds.length / 100) }, async (_, index) => {
      const response = await fetch(endpoint, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, cache: 'no-store',
        body: JSON.stringify({ variantIds: variantIds.slice(index * 100, (index + 1) * 100) }),
        signal: AbortSignal.timeout(15_000),
      });
      if (!response.ok) throw new Error('Availability request failed');
      const data = await response.json() as { items?: Array<{ variantId: string; purchaseBlocked: boolean; unavailableMessage: string | null }> };
      if (!Array.isArray(data.items)) throw new Error('Availability response incomplete');
      return data.items;
    }));
    const items = responses.flat();
    for (const line of checkedLines) {
      const availability = items.find(item => item.variantId === line.variantId);
      if (!availability || typeof availability.purchaseBlocked !== 'boolean') return AVAILABILITY_CHECK_FAILED;
      if (availability.purchaseBlocked) {
        return `${line.productTitle}: ${availability.unavailableMessage?.trim() || DEFAULT_UNAVAILABLE_MESSAGE}`;
      }
    }
    return null;
  } catch {
    return AVAILABILITY_CHECK_FAILED;
  }
}
